import React, { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { CircularProgress } from '@material-ui/core'
import { Link as RouterLink } from 'react-router-dom'
import { Panel, colors } from 'src/components/app-ui'
import UserType from 'src/model/user-types'
import {
  listApiKeysService,
  createApiKeyService,
  revokeApiKeyService,
} from 'src/api/api-key'
import { formatShortDate } from 'src/utils/library-sort'
import { fToNow } from 'src/utils/formatTime'
import safeTrack from 'src/utils/safe-track'
import Dialog from './dialog'
import useStyles from './styles'

// Offered expiries, in days. "Never" is the permissive default the API also defaults to.
const EXPIRY_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
]

const STATUS = {
  Active: { color: colors.successDark, background: '#E8FAEE' },
  Expired: { color: colors.warningDark, background: '#FFF4E5' },
  Revoked: { color: colors.muted, background: colors.chip },
}

const GRID = {
  gridTemplateColumns:
    'minmax(140px,1.2fr) minmax(170px,1.2fr) 100px 110px 110px 110px 80px',
  minWidth: 900,
}

const statusOf = apiKey => {
  if (apiKey.revokedAt) return 'Revoked'
  return apiKey.isActive ? 'Active' : 'Expired'
}

/** Converts the selected retention in days into the absolute instant the API expects. */
const expiresAtFromDays = days => {
  if (!days) return null
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

/**
 * Integrations › API keys: keys for the v2.1 API (MCG CLI, MCP server and
 * custom scripts). The full key is shown once, right after it is created.
 */
// API keys come with the Enterprise plan.
const API_KEY_PLANS = ['Enterprise', 'Super Admin']

const ApiKeysView = () => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const allowed = API_KEY_PLANS.includes(
    UserType.getRoleName(window.CONSTANTS?.USER_ROLE_ID),
  )

  const [apiKeys, setApiKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)

  // The open create form; null when closed.
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  // The one-time plaintext key returned by create, until the user closes it.
  const [createdKey, setCreatedKey] = useState(null)

  const [revokeTarget, setRevokeTarget] = useState(null)
  const [revoking, setRevoking] = useState(false)

  const fetchApiKeys = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listApiKeysService()
      setApiKeys(res.data || [])
    } catch (err) {
      if (err?.type === 'ForbiddenError') {
        setForbidden(true)
      } else {
        enqueueSnackbar('Failed to load API keys', { variant: 'error' })
      }
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => {
    if (allowed) fetchApiKeys()
    else setLoading(false)
  }, [allowed, fetchApiKeys])

  const name = form ? form.name.trim() : ''

  const create = async () => {
    setSaving(true)
    try {
      const res = await createApiKeyService({
        name,
        expiresAt: expiresAtFromDays(form.expiresInDays),
      })
      safeTrack('created_api_key')
      setForm(null)
      setCreatedKey(res.data?.key)
      await fetchApiKeys()
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to create API key', {
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey)
      enqueueSnackbar('Copied', { variant: 'success' })
    } catch (_e) {
      // Clipboard is unavailable over plain http or without permission; the
      // key stays selectable on screen.
    }
  }

  const confirmRevoke = async () => {
    setRevoking(true)
    try {
      await revokeApiKeyService(revokeTarget.id)
      enqueueSnackbar('API key revoked', { variant: 'success' })
      setRevokeTarget(null)
      await fetchApiKeys()
    } catch (err) {
      enqueueSnackbar('Failed to revoke API key', { variant: 'error' })
    } finally {
      setRevoking(false)
    }
  }

  if (!allowed) {
    return (
      <div className={classes.page}>
        <div className={classes.pageHead}>
          <div>
            <div className={classes.pageTitle}>API keys</div>
            <div className={classes.pageDesc}>
              Use API keys to call the Mini Course Generator API from your own
              scripts and tools.
            </div>
          </div>
        </div>
        <Panel className={classes.locked}>
          <Icon
            icon="tabler:lock"
            width={32}
            height={32}
            color={colors.subtle}
          />
          <div className={classes.lockedTitle}>
            API keys are available on the Enterprise plan
          </div>
          <RouterLink to="/pricing" className={classes.lockedLink}>
            See plans
          </RouterLink>
        </Panel>
      </div>
    )
  }

  if (forbidden) {
    return (
      <div className={classes.notice}>
        You do not have permission to manage API keys for this workspace.
      </div>
    )
  }

  return (
    <div className={classes.page}>
      <div className={classes.pageHead}>
        <div>
          <div className={classes.pageTitle}>API keys</div>
          <div className={classes.pageDesc}>
            Use API keys to call the Mini Course Generator API from your own
            scripts and tools.
          </div>
        </div>
        <button
          type="button"
          className={classes.addButton}
          onClick={() => setForm({ name: '', expiresInDays: 0 })}
        >
          Create API key
        </button>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : (
        <Panel className={classes.table}>
          <div className={classes.tableHead} style={GRID}>
            <span>Name</span>
            <span>Key</span>
            <span>Status</span>
            <span>Created</span>
            <span>Last used</span>
            <span>Expires</span>
            <span className={classes.alignRight}>Actions</span>
          </div>
          {apiKeys.map(apiKey => {
            const status = statusOf(apiKey)
            return (
              <div key={apiKey.id} className={classes.tableRow} style={GRID}>
                <span className={classes.name}>{apiKey.name}</span>
                <span className={classes.mono}>{apiKey.prefix}••••</span>
                <span className={classes.statusPill} style={STATUS[status]}>
                  {status}
                </span>
                <span className={classes.cellMuted}>
                  {formatShortDate(apiKey.createdAt) || '—'}
                </span>
                <span className={classes.cellMuted}>
                  {apiKey.lastUsedAt ? fToNow(apiKey.lastUsedAt) : 'Never'}
                </span>
                <span className={classes.cellMuted}>
                  {apiKey.expiresAt
                    ? formatShortDate(apiKey.expiresAt)
                    : 'Never'}
                </span>
                <span className={classes.alignRight}>
                  {status === 'Active' && (
                    <button
                      type="button"
                      className={classes.revokeButton}
                      onClick={() => setRevokeTarget(apiKey)}
                    >
                      Revoke
                    </button>
                  )}
                </span>
              </div>
            )
          })}
          {apiKeys.length === 0 && (
            <div className={classes.empty}>No API keys yet.</div>
          )}
        </Panel>
      )}

      {form && (
        <Dialog
          onClose={() => !saving && setForm(null)}
          className={classes.formDialog}
        >
          <div className={classes.formDialogTitle}>Create API key</div>
          <label className={classes.field}>
            Name
            <input
              className={classes.input}
              value={form.name}
              onChange={event => setForm({ ...form, name: event.target.value })}
              placeholder="CI pipeline"
              maxLength={128}
              autoFocus
            />
          </label>
          <label className={classes.field}>
            Expires
            <div className={classes.selectWrap}>
              <select
                className={classes.select}
                value={form.expiresInDays}
                onChange={event =>
                  setForm({
                    ...form,
                    expiresInDays: Number(event.target.value),
                  })
                }
              >
                {EXPIRY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className={classes.chevron}>
                <Icon icon="tabler:chevron-down" width={16} height={16} />
              </span>
            </div>
          </label>
          <div className={classes.dialogActions}>
            <button
              type="button"
              className={classes.textButton}
              disabled={saving}
              onClick={() => setForm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.primaryButton}
              disabled={!name || saving}
              onClick={create}
            >
              Create
            </button>
          </div>
        </Dialog>
      )}

      {createdKey && (
        <Dialog className={`${classes.formDialog} ${classes.keyDialog}`}>
          <div className={classes.formDialogTitle}>Copy your API key</div>
          <div className={classes.warning}>
            <Icon icon="tabler:alert-triangle" width={18} height={18} />
            This is the only time the full key is shown. Copy it and store it
            somewhere safe.
          </div>
          <div className={classes.secret}>
            <code className={classes.secretText}>{createdKey}</code>
            <button type="button" className={classes.copyButton} onClick={copy}>
              <Icon icon="tabler:copy" width={16} height={16} />
              Copy
            </button>
          </div>
          <div className={classes.keyDone}>
            <button
              type="button"
              className={classes.outlineButton}
              onClick={() => setCreatedKey(null)}
            >
              Done
            </button>
          </div>
        </Dialog>
      )}

      {revokeTarget && (
        <Dialog onClose={() => !revoking && setRevokeTarget(null)}>
          <div className={classes.dialogTitle}>Revoke {revokeTarget.name}?</div>
          <div className={classes.dialogText}>
            Anything using this key stops working right away. This cannot be
            undone.
          </div>
          <div className={classes.dialogActions}>
            <button
              type="button"
              className={classes.textButton}
              disabled={revoking}
              onClick={() => setRevokeTarget(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.dangerButton}
              disabled={revoking}
              onClick={confirmRevoke}
            >
              Revoke
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default ApiKeysView
